import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Clipboard,
  Clock,
  Globe,
  Link as LinkIcon,
  RefreshCw,
  Settings,
  Timer
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import TimezoneSelector from './TimezoneSelector';
import { appointmentsAPI, handleApiError } from '../../utils/api';

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (error) {
    return 'UTC';
  }
};

const toIntOrDefault = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const sanitizeSlug = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const normalizeSettings = (raw, detectedTimezone) => {
  const maxAdvanceDaysFromHours = raw?.max_advance_booking
    ? Math.max(1, Math.ceil(toIntOrDefault(raw.max_advance_booking, 720) / 24))
    : null;

  return {
    duration: toIntOrDefault(raw?.duration ?? raw?.booking_duration, 30),
    break_time: toIntOrDefault(raw?.break_time ?? raw?.buffer_time, 15),
    min_notice_hours: toIntOrDefault(raw?.min_notice_hours ?? raw?.min_advance_booking, 24),
    max_advance_days: toIntOrDefault(raw?.max_advance_days, maxAdvanceDaysFromHours || 30),
    booking_url_slug: sanitizeSlug(raw?.booking_url_slug || ''),
    is_active: typeof raw?.is_active === 'boolean' ? raw.is_active : true,
    time_zone: raw?.time_zone || detectedTimezone || 'UTC'
  };
};

const DEFAULT_SETTINGS = normalizeSettings({}, detectTimezone());

const BookingSettings = ({ showToast }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [generatingSlug, setGeneratingSlug] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(true);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [baseline, setBaseline] = useState(DEFAULT_SETTINGS);

  const bookingUrl = useMemo(() => {
    if (!settings.booking_url_slug) return '';
    return `${window.location.origin}/rdv/${settings.booking_url_slug}`;
  }, [settings.booking_url_slug]);

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(baseline),
    [settings, baseline]
  );

  const notify = useCallback(
    (message, type = 'info') => {
      if (!showToast) return;
      showToast(message, type);
    },
    [showToast]
  );

  const fetchSettings = useCallback(async () => {
    const detectedTimezone = detectTimezone();

    try {
      setLoading(true);
      const response = await appointmentsAPI.getBookingSettings();
      const normalized = normalizeSettings(response.data || {}, detectedTimezone);
      setSettings(normalized);
      setBaseline(normalized);
      setSlugAvailable(true);
    } catch (error) {
      notify(
        handleApiError(
          error,
          t('appointments.settings.error.fetch', 'Unable to load booking settings')
        ),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = useCallback(
    async (nextSettings, { silent = false } = {}) => {
      if (nextSettings.booking_url_slug && !slugAvailable) {
        if (!silent) {
          notify(
            t('appointments.settings.error.invalidSlugState', 'Please choose an available URL slug'),
            'error'
          );
        }
        return false;
      }

      try {
        setSaving(true);
        const payload = {
          duration: nextSettings.duration,
          break_time: nextSettings.break_time,
          min_notice_hours: nextSettings.min_notice_hours,
          max_advance_days: nextSettings.max_advance_days,
          booking_url_slug: nextSettings.booking_url_slug || null,
          is_active: nextSettings.is_active,
          time_zone: nextSettings.time_zone
        };

        const response = await appointmentsAPI.saveBookingSettings(payload);
        const normalized = normalizeSettings(response.data || payload, detectTimezone());
        setSettings(normalized);
        setBaseline(normalized);
        if (!silent) {
          notify(
            t('appointments.settings.success.save', 'Booking settings saved successfully'),
            'success'
          );
        }
        return true;
      } catch (error) {
        if (!silent) {
          notify(
            handleApiError(
              error,
              t('appointments.settings.error.save', 'Unable to save booking settings')
            ),
            'error'
          );
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [notify, slugAvailable, t]
  );

  useEffect(() => {
    if (loading || !isDirty) return;
    if (settings.booking_url_slug && !slugAvailable) return;

    const timer = setTimeout(() => {
      saveSettings(settings, { silent: true });
    }, 1400);

    return () => clearTimeout(timer);
  }, [isDirty, loading, saveSettings, settings, slugAvailable]);

  useEffect(() => {
    if (!settings.booking_url_slug) {
      setSlugAvailable(true);
      setCheckingSlug(false);
      return undefined;
    }

    if (settings.booking_url_slug === baseline.booking_url_slug) {
      setSlugAvailable(true);
      setCheckingSlug(false);
      return undefined;
    }

    if (settings.booking_url_slug.length < 3) {
      setSlugAvailable(false);
      setCheckingSlug(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setCheckingSlug(true);
        const response = await appointmentsAPI.checkBookingSlug(settings.booking_url_slug);
        setSlugAvailable(Boolean(response.data?.available));
      } catch (error) {
        setSlugAvailable(false);
      } finally {
        setCheckingSlug(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [baseline.booking_url_slug, settings.booking_url_slug]);

  const handleGenerateSlug = async () => {
    try {
      setGeneratingSlug(true);
      const response = await appointmentsAPI.generateBookingSlug();
      const generated = sanitizeSlug(response.data?.slug || '');
      if (!generated) {
        throw new Error('empty_slug');
      }
      setSettings((prev) => ({ ...prev, booking_url_slug: generated }));
      setSlugAvailable(true);
    } catch (error) {
      notify(
        handleApiError(error, t('appointments.settings.error.generateSlug', 'Unable to generate URL')),
        'error'
      );
    } finally {
      setGeneratingSlug(false);
    }
  };

  const handleCopyUrl = () => {
    if (!bookingUrl) return;
    navigator.clipboard.writeText(bookingUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1800);
    notify(t('appointments.settings.success.copy', 'Booking URL copied'), 'success');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Settings className="h-5 w-5 text-indigo-600" />
              {t('appointments.settings.systemStatus', 'Booking system status')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {settings.is_active
                ? t(
                    'appointments.settings.statusActive',
                    'Candidates can book appointments from your public link'
                  )
                : t(
                    'appointments.settings.statusInactive',
                    'Public booking is currently disabled'
                  )}
            </p>
          </div>

          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.is_active}
              onChange={(event) =>
                setSettings((prev) => ({ ...prev, is_active: event.target.checked }))
              }
              className="sr-only peer"
            />
            <div className="relative w-12 h-7 bg-gray-200 rounded-full peer-checked:bg-indigo-600 transition-colors after:content-[''] after:absolute after:top-1 after:left-1 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
          </label>
        </div>
      </section>

      <section className={`bg-white rounded-lg border border-gray-200 p-5 ${!settings.is_active ? 'opacity-60' : ''}`}>
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="h-4 w-4 text-indigo-600" />
          {t('appointments.settings.configTitle', 'Scheduling configuration')}
        </h3>
        <p className="text-sm text-gray-600 mt-1 mb-4">
          {t(
            'appointments.settings.configDescription',
            'Define slot duration, booking notice and planning window'
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('appointments.settings.duration', 'Appointment duration')}
            </label>
            <select
              value={settings.duration}
              onChange={(event) =>
                setSettings((prev) => ({ ...prev, duration: toIntOrDefault(event.target.value, 30) }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              disabled={!settings.is_active}
            >
              <option value={15}>15 {t('common.minutes', 'minutes')}</option>
              <option value={30}>30 {t('common.minutes', 'minutes')}</option>
              <option value={45}>45 {t('common.minutes', 'minutes')}</option>
              <option value={60}>60 {t('common.minutes', 'minutes')}</option>
              <option value={90}>90 {t('common.minutes', 'minutes')}</option>
              <option value={120}>120 {t('common.minutes', 'minutes')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('appointments.settings.bufferTime', 'Buffer between meetings')}
            </label>
            <select
              value={settings.break_time}
              onChange={(event) =>
                setSettings((prev) => ({ ...prev, break_time: toIntOrDefault(event.target.value, 15) }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              disabled={!settings.is_active}
            >
              <option value={0}>{t('appointments.settings.noBreak', 'No break')}</option>
              <option value={5}>5 {t('common.minutes', 'minutes')}</option>
              <option value={10}>10 {t('common.minutes', 'minutes')}</option>
              <option value={15}>15 {t('common.minutes', 'minutes')}</option>
              <option value={30}>30 {t('common.minutes', 'minutes')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Timer className="inline h-4 w-4 mr-1 text-indigo-600" />
              {t('appointments.settings.minAdvance', 'Minimum notice')}
            </label>
            <select
              value={settings.min_notice_hours}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  min_notice_hours: toIntOrDefault(event.target.value, 24)
                }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              disabled={!settings.is_active}
            >
              <option value={1}>1 {t('common.hour', 'hour')}</option>
              <option value={2}>2 {t('common.hours', 'hours')}</option>
              <option value={6}>6 {t('common.hours', 'hours')}</option>
              <option value={12}>12 {t('common.hours', 'hours')}</option>
              <option value={24}>24 {t('common.hours', 'hours')}</option>
              <option value={48}>48 {t('common.hours', 'hours')}</option>
              <option value={72}>72 {t('common.hours', 'hours')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('appointments.settings.maxAdvance', 'Maximum advance booking')}
            </label>
            <select
              value={settings.max_advance_days}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  max_advance_days: toIntOrDefault(event.target.value, 30)
                }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              disabled={!settings.is_active}
            >
              <option value={7}>7 {t('common.days', 'days')}</option>
              <option value={14}>14 {t('common.days', 'days')}</option>
              <option value={30}>30 {t('common.days', 'days')}</option>
              <option value={60}>60 {t('common.days', 'days')}</option>
              <option value={90}>90 {t('common.days', 'days')}</option>
              <option value={180}>180 {t('common.days', 'days')}</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="inline h-4 w-4 mr-1 text-indigo-600" />
              {t('appointments.settings.timezone', 'Timezone')}
            </label>
            <TimezoneSelector
              value={settings.time_zone}
              onChange={(timezone) => setSettings((prev) => ({ ...prev, time_zone: timezone }))}
              showLabel={false}
              showDetectedOption
            />
          </div>
        </div>
      </section>

      <section className={`bg-white rounded-lg border border-gray-200 p-5 ${!settings.is_active ? 'opacity-60' : ''}`}>
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <LinkIcon className="h-4 w-4 text-indigo-600" />
          {t('appointments.settings.urlTitle', 'Public booking URL')}
        </h3>
        <p className="text-sm text-gray-600 mt-1 mb-4">
          {t(
            'appointments.settings.urlDescription',
            'Customize and share your booking link with candidates'
          )}
        </p>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('appointments.settings.customUrl', 'Custom URL slug')}
          </label>

          <div className="flex">
            <span className="inline-flex items-center px-3 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-sm text-gray-500">
              {window.location.origin}/rdv/
            </span>
            <input
              type="text"
              value={settings.booking_url_slug}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  booking_url_slug: sanitizeSlug(event.target.value)
                }))
              }
              placeholder="my-booking-link"
              className={`flex-1 border rounded-r-lg px-3 py-2 text-sm ${
                !slugAvailable && settings.booking_url_slug ? 'border-red-300' : 'border-gray-200'
              }`}
              disabled={!settings.is_active}
            />
            <button
              type="button"
              onClick={handleGenerateSlug}
              disabled={generatingSlug || !settings.is_active}
              className="ml-2 inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              aria-label={t('appointments.settings.generateUrl', 'Generate URL')}
            >
              <RefreshCw className={`h-4 w-4 ${generatingSlug ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {checkingSlug && (
            <p className="text-sm text-gray-500 inline-flex items-center">
              <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />
              {t('appointments.settings.checkingAvailability', 'Checking availability...')}
            </p>
          )}

          {!checkingSlug && settings.booking_url_slug && !slugAvailable && (
            <p className="text-sm text-red-600 inline-flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1.5" />
              {t('appointments.settings.urlTaken', 'This URL is already in use')}
            </p>
          )}

          {!checkingSlug && settings.booking_url_slug && slugAvailable && (
            <p className="text-sm text-green-600 inline-flex items-center">
              <Check className="h-4 w-4 mr-1.5" />
              {t('appointments.settings.urlAvailable', 'This URL is available')}
            </p>
          )}
        </div>

        {bookingUrl && slugAvailable && (
          <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
            <p className="text-xs uppercase tracking-wide text-indigo-700 font-medium">
              {t('appointments.settings.yourUrl', 'Your booking URL')}
            </p>
            <p className="text-sm text-indigo-900 font-mono break-all mt-1">{bookingUrl}</p>
            <div className="mt-3">
              <button
                type="button"
                onClick={handleCopyUrl}
                className="inline-flex items-center rounded-lg border border-indigo-200 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
              >
                {copySuccess ? <Check className="h-4 w-4 mr-1.5" /> : <Clipboard className="h-4 w-4 mr-1.5" />}
                {copySuccess ? t('common.copied', 'Copied') : t('common.copy', 'Copy link')}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-600">
            {saving ? (
              <span className="inline-flex items-center">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin text-indigo-600" />
                {t('appointments.settings.autoSaving', 'Saving changes...')}
              </span>
            ) : isDirty ? (
              t('appointments.settings.unsavedChanges', 'Unsaved changes')
            ) : (
              t('appointments.settings.savedState', 'All changes are saved')
            )}
          </div>

          <button
            type="button"
            onClick={() => saveSettings(settings, { silent: false })}
            disabled={saving || !isDirty || (settings.booking_url_slug && !slugAvailable)}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                {t('common.saving', 'Saving...')}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t('common.save', 'Save')}
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
};

export default BookingSettings;
